async function runPool<T>(
  items: readonly T[],
  concurrency: number,
  run: (item: T, index: number) => Promise<void>,
) {
  const limit = Math.max(1, Math.floor(concurrency));
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      await run(items[currentIndex], currentIndex);
    }
  }

  const workerCount = Math.min(limit, items.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
}

/**
 * Runs `mapper` over `items` while keeping at most `concurrency` promises in
 * flight. Results stay in input order.
 *
 * Prefer this over an unbounded `Promise.all` for browser-bound work (uploads,
 * fan-out API calls) where the host connection limit would otherwise queue or
 * drop requests.
 *
 * Rejects on the first failure like `Promise.all`, which also stops the pool
 * from starting any item that has not been picked up yet. Use
 * `mapWithConcurrencySettled` when every item must be attempted.
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  concurrency: number,
  mapper: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results: R[] = Array.from({ length: items.length });

  await runPool(items, concurrency, async (item, index) => {
    results[index] = await mapper(item, index);
  });

  return results;
}

/**
 * `mapWithConcurrency` with `Promise.allSettled` semantics: a rejection is
 * recorded against its item and the pool keeps draining the queue.
 */
export async function mapWithConcurrencySettled<T, R>(
  items: readonly T[],
  concurrency: number,
  mapper: (item: T, index: number) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> {
  const results: PromiseSettledResult<R>[] = Array.from({
    length: items.length,
  });

  await runPool(items, concurrency, async (item, index) => {
    try {
      results[index] = {
        status: 'fulfilled',
        value: await mapper(item, index),
      };
    } catch (reason) {
      results[index] = { status: 'rejected', reason };
    }
  });

  return results;
}
